import React, { useState, useEffect } from 'react';
import { fetchAllTransactions, fetchTransactionDetails } from '../../services/apiService';
import BASE_URL_API from '../../services/baseUrl';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const SERVER_URL = BASE_URL_API.replace(/\/api\/?$/, '');
const PER_PAGE = 20;
const PERIODS = [
    { label: 'All Time',   value: '' },
    { label: 'Today',      value: 'today' },
    { label: 'This Week',  value: 'week' },
    { label: 'This Month', value: 'month' },
    { label: 'This Year',  value: 'year' },
];

const statusBadgeClass = (status) => {
    const map = {
        completed: 'bg-success',
        pending: 'bg-warning text-dark',
        failed: 'bg-danger',
        cancelled: 'bg-secondary',
        expired: 'bg-secondary',
        active: 'bg-primary',
    };
    return map[status?.toLowerCase()] || 'bg-secondary';
};

const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';
const formatAmount = (a) => (a !== null && a !== undefined) ? `$${Number(a).toFixed(2)}` : '—';

const DetailRow = ({ label, value }) => (
    <div className="col-md-6">
        <p className="text-muted small text-uppercase fw-semibold mb-1">{label}</p>
        <p className="fw-semibold mb-0 text-break">{value || '—'}</p>
    </div>
);

const Transaction = () => {
    const token = localStorage.getItem('accessToken');

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
    const [period, setPeriod] = useState('');

    const [showDetail, setShowDetail] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const loadTransactions = async (page = 1, p = period) => {
        setLoading(true);
        try {
            const res = await fetchAllTransactions(token, page, PER_PAGE, '', '', p);
            setTransactions(res.data.transactions);
            setPagination(res.data.pagination);
        } catch {
            toast.error('Failed to load transactions');
        } finally {
            setLoading(false);
        }
    };

    const handlePeriod = (p) => {
        setPeriod(p);
        loadTransactions(1, p);
    };

    useEffect(() => { loadTransactions(1, ''); }, []);

    const openDetail = async (txn) => {
        setShowDetail(true);
        setDetailData(null);
        setDetailLoading(true);
        try {
            const res = await fetchTransactionDetails(token, txn._id || txn.id);
            setDetailData(res.data.transaction);
        } catch {
            toast.error('Failed to load transaction details');
            setShowDetail(false);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetail = () => {
        setShowDetail(false);
        setDetailData(null);
    };

    // Stats from current page
    const totalVolume = transactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    const expiredCount = transactions.filter(t => t.status?.toLowerCase() === 'expired').length;
    const completedCount = transactions.filter(t => t.status?.toLowerCase() === 'completed').length;

    return (
        <div className="container-fluid py-4 px-3 px-md-4">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                <div>
                    <h2 className="fw-bold mb-0">Transactions</h2>
                    <p className="text-muted small mb-0">{pagination.total} total transactions</p>
                </div>
                <div className="btn-group" role="group">
                    {PERIODS.map(p => (
                        <button
                            key={p.value}
                            type="button"
                            className={`btn btn-sm ${period === p.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => handlePeriod(p.value)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="row g-3 mb-4">
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Total</p>
                            <h3 className="fw-bold text-primary mb-0">{pagination.total}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Page Volume</p>
                            <h3 className="fw-bold text-success mb-0">{formatAmount(totalVolume)}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Completed</p>
                            <h3 className="fw-bold text-success mb-0">{completedCount}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Expired</p>
                            <h3 className="fw-bold text-secondary mb-0">{expiredCount}</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Card */}
            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <div className="spinner-border text-primary me-2" role="status"></div>
                            <span className="text-muted">Loading transactions...</span>
                        </div>
                    ) : transactions.length === 0 ? (
                        <div className="text-center py-5">
                            <div className="fs-1 mb-2">💳</div>
                            <p className="text-muted fw-semibold mb-1">No transactions yet</p>
                            <p className="text-muted small">Transactions will appear here once activity begins.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-3">#</th>
                                        <th>Invoice ID</th>
                                        <th className="d-none d-sm-table-cell">Player</th>
                                        <th className="d-none d-md-table-cell">Game</th>
                                        <th className="d-none d-md-table-cell">Room</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th className="d-none d-lg-table-cell">Expires At</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((txn, i) => (
                                        <tr key={txn._id || txn.id}>
                                            <td className="ps-3 text-muted small">
                                                {(pagination.page - 1) * PER_PAGE + i + 1}
                                            </td>
                                            <td>
                                                <a
                                                    href={txn.invoiceUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-decoration-none fw-semibold small"
                                                    title={txn.invoiceId}
                                                >
                                                    {txn.invoiceId?.slice(0, 14)}…
                                                </a>
                                            </td>
                                            <td className="d-none d-sm-table-cell">
                                                <span className="fw-semibold small">{txn.playerUsername || '—'}</span>
                                            </td>
                                            <td className="d-none d-md-table-cell small text-muted">
                                                {txn.session?.game?.name || '—'}
                                            </td>
                                            <td className="d-none d-md-table-cell small text-muted">
                                                {txn.session?.room?.name || '—'}
                                            </td>
                                            <td className="fw-semibold">
                                                {formatAmount(txn.amount)}
                                                <span className="text-muted small ms-1">{txn.currency}</span>
                                            </td>
                                            <td>
                                                <span className={`badge ${statusBadgeClass(txn.status)}`}>
                                                    {txn.status || '—'}
                                                </span>
                                            </td>
                                            <td className="d-none d-lg-table-cell text-muted small">
                                                {formatDate(txn.expiresAt)}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => openDetail(txn)}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && transactions.length > 0 && (
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-3 border-top">
                            <span className="text-muted small">
                                Page {pagination.page} of {pagination.pages} · {pagination.total} total
                            </span>
                            <nav>
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => loadTransactions(pagination.page - 1)}>
                                            &laquo; Prev
                                        </button>
                                    </li>
                                    {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                                        .filter(p => Math.abs(p - pagination.page) <= 2)
                                        .map(p => (
                                            <li key={p} className={`page-item ${p === pagination.page ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => loadTransactions(p)}>{p}</button>
                                            </li>
                                        ))
                                    }
                                    <li className={`page-item ${pagination.page >= pagination.pages ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => loadTransactions(pagination.page + 1)}>
                                            Next &raquo;
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>
            </div>

            {/* Transaction Detail Modal */}
            {showDetail && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title fw-bold">Transaction Details</h5>
                                    <button type="button" className="btn-close" onClick={closeDetail}></button>
                                </div>
                                <div className="modal-body">
                                    {detailLoading ? (
                                        <div className="d-flex justify-content-center align-items-center py-5">
                                            <div className="spinner-border text-primary me-2" role="status"></div>
                                            <span className="text-muted">Loading details...</span>
                                        </div>
                                    ) : detailData ? (
                                        <div className="row g-3">

                                            {/* Amount / Status / Currency highlight */}
                                            <div className="col-12">
                                                <div className="card border-0" style={{ backgroundColor: '#101828' }}>
                                                    <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
                                                        <div>
                                                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Amount</p>
                                                            <h3 className="fw-bold text-primary mb-0">
                                                                {formatAmount(detailData.amount)}
                                                                <span className="fs-6 text-muted ms-1 fw-normal">{detailData.currency}</span>
                                                            </h3>
                                                        </div>
                                                        <div>
                                                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Status</p>
                                                            <span className={`badge fs-6 ${statusBadgeClass(detailData.status)}`}>
                                                                {detailData.status || '—'}
                                                            </span>
                                                        </div>
                                                        {detailData.paidAt && (
                                                            <div>
                                                                <p className="text-muted small mb-1 text-uppercase fw-semibold">Paid At</p>
                                                                <p className="fw-semibold mb-0 small">{formatDate(detailData.paidAt)}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* QR Code */}
                                            {detailData.qrCode && (
                                                <div className="col-12 text-center">
                                                    <p className="text-muted small text-uppercase fw-semibold mb-2">Payment QR Code</p>
                                                    <img
                                                        src={detailData.qrCode}
                                                        alt="QR Code"
                                                        className="img-thumbnail"
                                                        style={{ maxWidth: 180 }}
                                                    />
                                                    <div className="mt-2">
                                                        <a
                                                            href={detailData.invoiceUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="btn btn-sm btn-outline-primary"
                                                        >
                                                            Open Invoice ↗
                                                        </a>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Invoice Info */}
                                            <div className="col-12">
                                                <hr className="my-1" />
                                                <p className="fw-bold mb-2 mt-2">Invoice Information</p>
                                            </div>
                                            <DetailRow label="Transaction ID" value={detailData._id || detailData.id} />
                                            <DetailRow label="Invoice ID" value={detailData.invoiceId} />
                                            <div className="col-md-6">
                                                <p className="text-muted small text-uppercase fw-semibold mb-1">Invoice URL</p>
                                                <a
                                                    href={detailData.invoiceUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="small text-break"
                                                >
                                                    {detailData.invoiceUrl}
                                                </a>
                                            </div>
                                            <DetailRow label="Player" value={detailData.playerUsername} />
                                            <DetailRow label="Expires At" value={formatDate(detailData.expiresAt)} />
                                            <DetailRow label="Created At" value={formatDate(detailData.createdAt)} />
                                            <DetailRow label="Updated At" value={formatDate(detailData.updatedAt)} />

                                            {/* Crypto Info */}
                                            {(detailData.cryptoCurrency || detailData.cryptoAmount || detailData.cryptoAddress) && (
                                                <>
                                                    <div className="col-12">
                                                        <hr className="my-1" />
                                                        <p className="fw-bold mb-2 mt-2">Crypto Details</p>
                                                    </div>
                                                    <DetailRow label="Crypto Currency" value={detailData.cryptoCurrency} />
                                                    <DetailRow label="Crypto Amount" value={detailData.cryptoAmount} />
                                                    <DetailRow label="Crypto Address" value={detailData.cryptoAddress} />
                                                    <DetailRow label="Transaction Hash" value={detailData.transactionHash} />
                                                </>
                                            )}

                                            {/* Session Info */}
                                            {detailData.session && (
                                                <>
                                                    <div className="col-12">
                                                        <hr className="my-1" />
                                                        <p className="fw-bold mb-2 mt-2">Session Information</p>
                                                    </div>
                                                    <DetailRow label="Session Code" value={detailData.session.sessionCode} />
                                                    <DetailRow label="Session Status" value={detailData.session.status} />
                                                    <DetailRow label="IP Address" value={detailData.session.ipAddress} />
                                                    <DetailRow label="Session Amount" value={formatAmount(detailData.session.amount)} />

                                                    {/* Game Info */}
                                                    {detailData.session.game && (
                                                        <>
                                                            <div className="col-12">
                                                                <hr className="my-1" />
                                                                <p className="fw-bold mb-2 mt-2">Game & Room</p>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <p className="text-muted small text-uppercase fw-semibold mb-1">Game</p>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {detailData.session.game.thumbnail && (
                                                                        <img
                                                                            src={`${SERVER_URL}${detailData.session.game.thumbnail}`}
                                                                            alt={detailData.session.game.name}
                                                                            className="rounded"
                                                                            style={{ width: 36, height: 36, objectFit: 'cover' }}
                                                                        />
                                                                    )}
                                                                    <span className="fw-semibold">{detailData.session.game.name}</span>
                                                                </div>
                                                            </div>
                                                            <DetailRow label="Room" value={detailData.session.room?.name} />
                                                        </>
                                                    )}
                                                </>
                                            )}

                                        </div>
                                    ) : null}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeDetail}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}
        </div>
    );
};

export default Transaction;